/* dd-glass-refraction.js
   Native liquid-glass refraction, progressive enhancement.

   Chromium supports SVG filters (feDisplacementMap) inside backdrop-filter,
   which refracts the LIVE backdrop every frame — no backdrop reconstruction,
   no snapshots, no WebGL. This helper builds a rounded-rect "lens" displacement
   map per element size, registers it as an SVG filter, and hands the filter id
   to CSS via --dd-glass-filter + the .dd-refract-on class. Component CSS slots
   the filter into its existing backdrop-filter chain.

   Safari/Firefox parse backdrop-filter: url() but silently drop displacement
   primitives, so @supports alone is not a reliable gate — we require Chromium
   (navigator.userAgentData is Chromium-only) AND the @supports check. Every
   other engine keeps the plain CSS frosted glass and never sees this class.

   Elements opt in with data-dd-glass-refract. Filters are cached by
   width x height x radius, so carousel clones (same size) share one filter. */
(function () {
  if (window._ddGlassRefractionInit) return;
  window._ddGlassRefractionInit = true;

  var BAND_MAX = 22; /* px edge band where refraction lives */
  var MAP_MAX = 256; /* longest generated map side; field is smooth, upscales clean */
  var NS = 'http://www.w3.org/2000/svg';

  var isChromium = !!(
    navigator.userAgentData &&
    navigator.userAgentData.brands &&
    navigator.userAgentData.brands.some(function (b) {
      return /Chromium/i.test(b.brand);
    })
  );
  if (!isChromium) return;
  if (!(window.CSS && CSS.supports && CSS.supports('backdrop-filter', 'url(#dd)'))) return;

  var svgRoot = null;
  var filterCache = Object.create(null);
  var uid = 0;

  function ensureSvgRoot() {
    if (svgRoot && svgRoot.isConnected) return svgRoot;
    svgRoot = document.createElementNS(NS, 'svg');
    svgRoot.setAttribute('width', '0');
    svgRoot.setAttribute('height', '0');
    svgRoot.setAttribute('aria-hidden', 'true');
    svgRoot.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
    document.body.appendChild(svgRoot);
    return svgRoot;
  }

  function radiusOf(el, w, h) {
    var raw = getComputedStyle(el).borderTopLeftRadius || '0px';
    var v = parseFloat(raw) || 0;
    if (raw.indexOf('%') !== -1) v = (Math.min(w, h) * v) / 100;
    return Math.min(v, Math.min(w, h) / 2);
  }

  /* Displacement map: R = x-shift, G = y-shift, 128 = neutral.
     Interior stays flat; a band at the edge displaces outward along the
     rounded-rect SDF normal with a cubic falloff — the same lens profile the
     old WebGL shader used (t^3 bulge over a ~22px band). feDisplacementMap
     computes P' = P + scale * (channel - 0.5), so outward-positive channels
     make edge pixels sample outside content = edge magnification. */
  function buildMapURI(w, h, r, band) {
    var s = Math.min(1, MAP_MAX / Math.max(w, h));
    var mw = Math.max(2, Math.round(w * s));
    var mh = Math.max(2, Math.round(h * s));
    var mr = r * s;
    var mb = Math.max(band * s, 1);
    var cx = mw / 2;
    var cy = mh / 2;
    var bx = cx - mr;
    var by = cy - mr;

    function sdf(x, y) {
      var px = Math.abs(x - cx) - bx;
      var py = Math.abs(y - cy) - by;
      var ox = Math.max(px, 0);
      var oy = Math.max(py, 0);
      return Math.min(Math.max(px, py), 0) + Math.hypot(ox, oy) - mr;
    }

    var canvas = document.createElement('canvas');
    canvas.width = mw;
    canvas.height = mh;
    var ctx = canvas.getContext('2d');
    var img = ctx.createImageData(mw, mh);
    var data = img.data;

    for (var y = 0; y < mh; y++) {
      for (var x = 0; x < mw; x++) {
        var fx = x + 0.5;
        var fy = y + 0.5;
        var d = sdf(fx, fy);
        var t = Math.min(Math.max(1 + d / mb, 0), 1);
        var bulge = t * t * t;
        var i = (y * mw + x) * 4;
        if (bulge > 0.001) {
          var nx = sdf(fx + 1, fy) - sdf(fx - 1, fy);
          var ny = sdf(fx, fy + 1) - sdf(fx, fy - 1);
          var nl = Math.hypot(nx, ny) || 1;
          data[i] = Math.round(128 + (nx / nl) * bulge * 127);
          data[i + 1] = Math.round(128 + (ny / nl) * bulge * 127);
        } else {
          data[i] = 128;
          data[i + 1] = 128;
        }
        data[i + 2] = 128;
        data[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    return canvas.toDataURL('image/png');
  }

  function filterFor(w, h, r) {
    var key = Math.round(w) + 'x' + Math.round(h) + 'x' + Math.round(r);
    if (filterCache[key]) return filterCache[key];

    var band = Math.min(BAND_MAX, Math.min(w, h) * 0.33);
    /* Max shift = scale * 0.5; keep it proportional to the band so a 36px
       arrow refracts gently and a large panel gets the full lens. */
    var scale = band * 1.2;

    var id = 'dd-glass-refract-' + ++uid;
    var filter = document.createElementNS(NS, 'filter');
    filter.setAttribute('id', id);
    filter.setAttribute('x', '0');
    filter.setAttribute('y', '0');
    filter.setAttribute('width', '100%');
    filter.setAttribute('height', '100%');
    /* sRGB keeps 128 = zero displacement; the default linearRGB re-maps
       channel values and skews the neutral point. */
    filter.setAttribute('color-interpolation-filters', 'sRGB');

    var feImage = document.createElementNS(NS, 'feImage');
    feImage.setAttribute('href', buildMapURI(w, h, r, band));
    feImage.setAttribute('x', '0');
    feImage.setAttribute('y', '0');
    feImage.setAttribute('width', String(w));
    feImage.setAttribute('height', String(h));
    feImage.setAttribute('preserveAspectRatio', 'none');
    feImage.setAttribute('result', 'map');

    var feDisp = document.createElementNS(NS, 'feDisplacementMap');
    feDisp.setAttribute('in', 'SourceGraphic');
    feDisp.setAttribute('in2', 'map');
    feDisp.setAttribute('scale', scale.toFixed(1));
    feDisp.setAttribute('xChannelSelector', 'R');
    feDisp.setAttribute('yChannelSelector', 'G');

    filter.appendChild(feImage);
    filter.appendChild(feDisp);
    ensureSvgRoot().appendChild(filter);
    filterCache[key] = id;
    return id;
  }

  function applyTo(el) {
    var rect = el.getBoundingClientRect();
    var w = rect.width;
    var h = rect.height;
    if (w < 4 || h < 4) return;
    var id = filterFor(w, h, radiusOf(el, w, h));
    el.style.setProperty('--dd-glass-filter', 'url(#' + id + ')');
    el.classList.add('dd-refract-on');
  }

  function initEl(el) {
    if (el._ddRefract) return;
    el._ddRefract = true;
    applyTo(el);
    /* Re-map on size change (accordion open/close, responsive relayout).
       Cached filters make repeat sizes free. */
    var timer = null;
    var ro = new ResizeObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        applyTo(el);
      }, 80);
    });
    ro.observe(el);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-dd-glass-refract]').forEach(initEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
